import { useMemo, useState } from "react";
import { LivingBackdrop } from "./LivingBackdrop";
import { EnergyNode } from "./EnergyNode";

export type SpiralPoint = {
  id: string;
  slug: string;
  label: string;
  hue: number;
  completed?: boolean;
  order: number;
};

type Props = {
  points: SpiralPoint[];
  onSelect?: (p: SpiralPoint) => void;
  selectedId?: string | null;
  height?: number;
};

/**
 * The living spiral. Every point rides a golden-ratio arc. Completed points
 * glow more brightly. Backdrop is calm and ambient. Reduced-motion aware.
 */
export function SpiralJourney({ points, onSelect, selectedId, height = 560 }: Props) {
  const cx = 300, cy = 300;
  const [hover, setHover] = useState<string | null>(null);

  const laidOut = useMemo(() => {
    const PHI = (1 + Math.sqrt(5)) / 2;
    const n = points.length || 1;
    return points
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((p, i) => {
        const t = (i + 1) / (n + 1);
        const angle = t * Math.PI * 2.4 - Math.PI / 2;
        const r = 60 + Math.pow(t, 1 / PHI) * 200;
        return {
          ...p,
          x: cx + Math.cos(angle) * r,
          y: cy + Math.sin(angle) * r,
        };
      });
  }, [points]);

  const spiralPath = useMemo(() => {
    const PHI = (1 + Math.sqrt(5)) / 2;
    const steps = 240;
    let d = "";
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const angle = t * 3 * Math.PI * 2 - Math.PI / 2;
      const r = Math.pow(t, 1 / PHI) * 260;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      d += (i === 0 ? "M" : "L") + x.toFixed(2) + "," + y.toFixed(2) + " ";
    }
    return d;
  }, []);

  return (
    <div className="relative overflow-hidden rounded-2xl border hairline bg-obsidian/60" style={{ height }}>
      <LivingBackdrop hue={44} density={70} />
      <svg viewBox="0 0 600 600" className="absolute inset-0 h-full w-full" role="img" aria-label="KAfrequency spiral journey">
        <path
          d={spiralPath}
          fill="none"
          stroke="hsl(44 85% 60% / 0.28)"
          strokeWidth={1}
          className="motion-safe:animate-[energy-rotate_180s_linear_infinite]"
          style={{ transformBox: "fill-box", transformOrigin: `${cx}px ${cy}px` }}
        />
        {[100, 160, 220, 280].map((r) => (
          <circle key={r} cx={cx} cy={cy} r={r} fill="none" stroke="hsl(44 85% 60% / 0.10)" />
        ))}
        {laidOut.map((p) => {
          const active = hover === p.id || selectedId === p.id;
          const size = p.completed ? 26 : 22;
          return (
            <g
              key={p.id}
              tabIndex={0}
              role="button"
              aria-label={p.label}
              onMouseEnter={() => setHover(p.id)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(p.id)}
              onBlur={() => setHover(null)}
              onClick={() => onSelect?.(p)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect?.(p);
                }
              }}
              className="cursor-pointer outline-none focus-visible:[&_circle]:stroke-gold"
            >
              <EnergyNode cx={p.x} cy={p.y} r={size} hue={p.hue} active={active} defined={p.completed} label="" />
              <text
                x={p.x}
                y={p.y + size + 14}
                textAnchor="middle"
                className="pointer-events-none fill-foreground/80"
                fontSize={11}
              >
                {p.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
